import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { ApiError } from '../../../lib/http/ApiError';
import { useNotification } from '../../notifications/hooks/useNotification';
import { usePermissions } from '../../permissions/usePermissions';
import { useAuth } from '../../auth/hooks/useAuth';
import { useCreateUser } from '../hooks/useCreateUser';
import { UserForm, type UserFormSubmit } from '../components/UserForm/UserForm';
import type { CreateUserPayload } from '../userTypes';

const USERS_LIST = '/my-ngo/users';

/**
 * Create page (HOMCO-623): hosts the shared UserForm, builds the `CreateUserPayload`
 * and posts it in a single atomic call. Success → toast + back to the list. A 409 is
 * surfaced on the username/email field (inputs preserved); a 403 (P5 self-escalation)
 * lands on the profile selector; other 4xx go to a form-level alert. 5xx are toasted
 * by the hook. The profiles dropdown and perimeter selector self-fetch inside the form.
 */
export function CreateUserPage() {
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { notify, notifySuccess } = useNotification();
  const { can } = usePermissions();
  const { isInitialized, role: callerRole } = useAuth();

  const { createUser, loading: creating, error: createError } = useCreateUser();

  // The create hook swallows 4xx (returns null, only exposing them via `error`) and
  // resets `error` to null at the start of each call — so we derive the field / form
  // errors from it during render (no effect, no local state).
  let usernameError: string | undefined;
  let emailError: string | undefined;
  let formError: string | undefined;
  if (createError && ApiError.isApiError(createError)) {
    const message = (createError.messages[0] ?? '').toLowerCase();
    if (createError.statusCode === 409 && message.includes('username')) {
      usernameError = t('form.usernameConflict');
    } else if (createError.statusCode === 409 && message.includes('email')) {
      emailError = t('form.emailConflict');
    } else if (createError.statusCode === 403) {
      formError = t('form.forbidden');
    } else if (createError.statusCode === 404) {
      formError = t('form.profileNotFound');
    } else if (createError.isValidationError && createError.details.errors) {
      const errs = createError.details.errors as Record<string, string[]>;
      if (errs.email) emailError = t('form.emailInvalid');
      if (errs.username) usernameError = t('form.usernameRequired');
      if (!emailError && !usernameError) formError = t('form.saveFailed');
    } else if (createError.isClientError) {
      console.error('[CreateUserPage] create failed:', createError.messages);
      formError = t('form.saveFailed');
    }
  }

  const handleSubmit = async (values: UserFormSubmit) => {
    // No password is chosen here (HOMCO-1393): the backend e-mails an activation
    // link to an active account and reports delivery in `invitation_email_sent`.
    const payload: CreateUserPayload = {
      email: values.email,
      username: values.username,
      perimeter_nodes: values.perimeter_nodes,
      is_active: values.is_active,
    };
    if (values.display_name) payload.display_name = values.display_name;
    // F3 (HOMCO-1376): role XOR profile_id. For the default `users` role, send only the
    // profile_id (the backend defaults the role to users, and would force a non-admin's
    // choice back to users anyway). For an admin/hq account (only an admin can pick one),
    // send the role and no profile_id (ignored server-side for those roles).
    if (values.role === 'users') payload.profile_id = values.profile_id;
    else payload.role = values.role;

    const created = await createUser(payload);
    if (created) {
      // An inactive account gets no e-mail by design — only a failed send warns.
      if (created.is_active && !created.invitation_email_sent) {
        notify({ message: t('create.emailNotSent'), severity: 'warning' });
      } else {
        notifySuccess(t('create.success'));
      }
      navigate(USERS_LIST);
    }
  };

  // Guard direct navigation: the backend refuses POST /users without users.create.
  // Decide only once the auth bootstrap has settled (isInitialized).
  if (isInitialized && !can('users', 'create')) {
    return <Navigate to={USERS_LIST} replace />;
  }

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <UserForm
      title={t('create.title')}
      subtitle={t('create.subtitle')}
      submitLabel={t('form.submit')}
      callerRole={callerRole ?? undefined}
      usernameError={usernameError}
      emailError={emailError}
      formError={formError}
      saving={creating}
      onSubmit={handleSubmit}
      onCancel={() => navigate(USERS_LIST)}
    />
  );
}
